import { Chart as ChartJS, ChartType, registerables } from "chart.js";
import { useLayoutEffect, useRef } from "react";

import { ChartConfiguration, ChartData } from "@/components/ui/Chart/chart.types";
import { CHART_FONT } from "@/components/ui/Chart/chart.utils";

import "chartjs-adapter-date-fns";

ChartJS.register(...registerables);
ChartJS.defaults.font = {
    family: CHART_FONT,
    size: 12,
    lineHeight: 1,
    weight: null,
    style: "normal",
};
ChartJS.defaults.color = "#000000";

type Props<TType extends ChartType = ChartType> = {
    configuration: ChartConfiguration<TType>;
    data: ChartData<TType>;
    onChartCreated?: (chart: ChartJS<TType, unknown> | null) => void;
};

const Chart = <TType extends ChartType = ChartType>(props: Props<TType>) => {
    const { configuration, data, onChartCreated } = props;
    const chartRef = useRef<HTMLCanvasElement>(null);

    const onChartCreatedRef = useRef(onChartCreated);
    onChartCreatedRef.current = onChartCreated;

    useLayoutEffect(() => {
        if (!chartRef.current) return;
        const ch = new ChartJS(chartRef.current, { ...configuration, data });
        onChartCreatedRef.current?.(ch);
        return () => {
            ch.destroy();
            onChartCreatedRef.current?.(null);
        };
    }, [configuration, data, onChartCreatedRef]);

    return <canvas ref={chartRef} />;
};

export { Chart };
export type { Props as ChartProps };
